import { useCallback, useState } from 'react'
import type { FC, PropsWithChildren } from 'react'
import Typography from '@mui/material/Typography'
import { Linkify, Loader, UserAvatar } from '~/shared/ui'
import { BRAND } from '~/shared/lib/theme'
import ChatReadReceipt from './ChatReadReceipt'
import { MessageIdentifier } from '~/entities/case'
import { useDrag } from 'react-dnd'
import { Badge, Box, SxProps, type Theme } from '@mui/material'
import MessageInfo from './MessageInfo'
import MessageActions from './MessageActions'
import { findMessageIdentifier, messageDateInfo } from './utils'
import { isUrgent } from '../chatUtils'
import { BffCrmPersonChatRetrieveResponse } from '@fireflyhealth/api'
import { useChat } from '../CommunicationThread'

type ChatCommunication = Extract<
  BffCrmPersonChatRetrieveResponse['communications']['results'][number]['content_object'],
  { kind: 'chat' }
>

interface Props {
  message: Pick<
    ChatCommunication,
    | 'kind'
    | 'id'
    | 'uid'
    | 'event'
    | 'is_urgent_message'
    | 'message_tags'
    | 'sent_at'
    | 'text'
    | 'attachment'
    | 'file_attachment'
  > & {
    sender: Pick<ChatCommunication['sender'], 'id' | 'avatar_url' | 'full_name_used' | 'is_patient'>
    case_relations: {
      case: Pick<ChatCommunication['case_relations'][number]['case'], 'id'> & {
        category: Pick<
          ChatCommunication['case_relations'][number]['case']['category'],
          'id' | 'title'
        >
        relations: (Pick<
          ChatCommunication['case_relations'][number]['case']['relations'][number],
          'object_id'
        > & {
          content_type: Pick<
            ChatCommunication['case_relations'][number]['case']['relations'][number]['content_type'],
            'model'
          >
        })[]
      }
    }[]
  }
  mine: boolean
  highlightChatMessageContent?: boolean
  person: Pick<BffCrmPersonChatRetrieveResponse['person'], 'id' | 'user'>
  patientReadAt: string | null
  isLastMessage: boolean
  threadUid: string | null
  canCase: boolean
  canUncase: boolean
  /** Whether this is an optimistic (not yet persisted) message */
  isOptimistic?: boolean
}

const ChatMessage: FC<PropsWithChildren<Props>> = props => {
  const {
    children,
    mine,
    message,
    highlightChatMessageContent,
    person,
    patientReadAt,
    isLastMessage,
    isOptimistic = false,
  } = props
  const { text, id, sender } = message

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'messageIdentifier',
      item: (): MessageIdentifier => {
        return findMessageIdentifier(message)
      },
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [message.id, message.uid],
  )
  // The connector returns an element, which React 19's ref callbacks may not;
  // memoised so the wrapper keeps the connector's stable identity and the ref
  // is not detached and reattached on every render.
  const dragRef = useCallback((el: HTMLDivElement | null) => void drag(el), [drag])

  /*
    While using the drag and drop library, we cannot select the text within the drag component, as it drags the component.
    To overcome this, the bubble is draggable only while the mouse is down on the bubble and not on its text.
  */
  const [mouseOnChatText, setMouseOnChatText] = useState<boolean>(false)
  const [mouseDownOnChatBox, setMouseDownOnChatBox] = useState<boolean>(false)
  const draggable = mouseDownOnChatBox && !mouseOnChatText

  // Get selection state from context
  const { selection } = useChat()
  const isSelectable = !isOptimistic
  const isMessageSelected = selection.isSelected(id)
  const isCopyFlash = selection.isCopyFlash

  const handleClick = (e: React.MouseEvent) => {
    if (isSelectable) {
      // Prevent text selection on shift+click
      e.preventDefault()
      selection.toggleSelect(id, e.shiftKey)
    }
  }

  const name = sender.full_name_used
  const avatarSrc = sender.avatar_url ?? undefined

  const { messageDate, preciseDate } = messageDateInfo(message.sent_at ?? 0)
  const messageVariant = mine ? 'Mine' : sender.is_patient ? 'Patient' : 'Provider'

  // Avoid the hover menu from overlapping with the DOM snapshot
  // during drag-and-drop
  const showMessageActions = !(isDragging || mouseDownOnChatBox)

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const messageTextSx: SxProps<Theme> = theme => ({
    position: 'relative' as const,
    padding: theme.spacing(1.25, 3),
    borderTopLeftRadius: theme.spacing(2),
    borderTopRightRadius: theme.spacing(2),
    wordBreak: 'break-word' as const,
    whiteSpace: 'pre-wrap' as const,
    fontSize: '1.6rem',
    ...(mine
      ? {
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: theme.spacing(2),
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
          '& a, & a[href].sw-phone:link': {
            color: `${theme.palette.common.white} !important`, // Dialpad makes their links unreadable blue and uses !important :rolls-eyes:
            textDecoration: 'underline',
          },
        }
      : sender.is_patient
        ? {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: theme.spacing(2),
            backgroundColor: theme.palette.grey[50],
            color: theme.palette.text.primary,
            '& a': { color: theme.palette.primary.main },
          }
        : {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: theme.spacing(2),
            backgroundColor: theme.palette.primary[100],
            color: theme.palette.text.primary,
            '& a': { color: theme.palette.primary.main },
          }),
  })

  // A finished drag releases the mouse-down that started it.
  const [wasDragging, setWasDragging] = useState(isDragging)
  if (isDragging !== wasDragging) {
    setWasDragging(isDragging)
    if (!isDragging) setMouseDownOnChatBox(false)
  }

  const messageContents = (
    <Typography
      style={{ cursor: 'text' }}
      onMouseOver={() => setMouseOnChatText(true)}
      onMouseOut={() => setMouseOnChatText(false)}
      onFocus={() => setMouseOnChatText(true)}
      onBlur={() => setMouseOnChatText(false)}
      // This is to allow selecting text content without triggering a "selection"
      // of the message object for quoting
      onClick={e => e.stopPropagation()}
    >
      {text}
    </Typography>
  )

  return (
    <Box sx={{ mt: highlightChatMessageContent ? 0 : 2 }}>
      <Box
        data-id={id}
        onClick={isSelectable ? handleClick : undefined}
        sx={[
          { display: 'flex', flexDirection: 'row', alignItems: 'flex-end' },
          theme => ({
            position: 'relative',
            outlineWidth: 3,
            outlineStyle: 'solid',
            ...(isMessageSelected
              ? {
                  outlineColor: isCopyFlash
                    ? theme.palette.success['light']
                    : theme.palette.primary.main,
                }
              : {
                  outlineColor: 'transparent',
                }),
            outlineOffset: 2,
            borderRadius: 2,
            transition: 'outline-color 0.1s ease-in-out',
            ...(highlightChatMessageContent
              ? {
                  borderRadius: '1rem',
                  padding: theme.spacing(1),
                  backgroundColor: theme.palette.background.highlight,
                }
              : {}),
            '&:hover .message-actions-hover-control': {
              opacity: '1 !important',
            },
          }),
        ]}
      >
        {/* Left: avatar */}
        <Box>
          <UserAvatar
            id={sender.id}
            avatarStyle={{
              margin: '0 1rem 0 0',
              order: -2,
            }}
            src={avatarSrc}
          />
        </Box>
        {/* Right: message contents */}
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', mb: 0.5 }}>
            {!mine && <Typography sx={{ fontSize: '1.2rem', mr: 2 }}>{name}</Typography>}
            <Typography title={preciseDate} sx={{ fontSize: '1.2rem' }}>
              {messageDate}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-start',
              flex: '0 0 100%',
            }}
          >
            {isLoading && <Loader />}
            <Box
              sx={{
                display: showMessageActions ? 'initial' : 'none',
              }}
            >
              <MessageActions
                isLastMessage={isLastMessage}
                message={message}
                person={person}
                setIsLoading={setIsLoading}
                threadUid={props.threadUid}
                canCase={props.canCase}
              />
            </Box>
            <Box
              role="button"
              tabIndex={0}
              {...(props.canCase
                ? {
                    ref: dragRef,
                    draggable,
                    onMouseDown: () => setMouseDownOnChatBox(!mouseOnChatText),
                    onMouseUp: () => setMouseDownOnChatBox(false),
                    onDragStart: (event: React.DragEvent<HTMLElement>) => {
                      // The `draggable` prop allows dragging even when the value is false
                      // Block drag events here instead
                      if (!draggable) event.preventDefault()
                    },
                  }
                : {})}
              sx={{
                justifyContent: 'flex-start',
                display: 'flex',
                flexWrap: 'wrap',
                position: 'relative',
                justifyItems: 'center',
              }}
            >
              <Box>
                {isUrgent(message) ? (
                  <Badge
                    variant="dot"
                    sx={{ '& .MuiBadge-badge': { backgroundColor: BRAND.main } }}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    <Linkify>
                      {text?.length ? (
                        <Box
                          className={`messageText messageText${messageVariant}`}
                          sx={messageTextSx}
                        >
                          {messageContents}
                        </Box>
                      ) : null}
                    </Linkify>
                  </Badge>
                ) : (
                  <Linkify>
                    {text?.length ? (
                      <Box
                        className={`messageText messageText${messageVariant}`}
                        sx={messageTextSx}
                      >
                        {messageContents}
                      </Box>
                    ) : null}
                  </Linkify>
                )}
                {children}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{ ml: 6, mt: 1 }}>
        <MessageInfo message={message} threadUid={props.threadUid} canUncase={props.canUncase} />
      </Box>
      {patientReadAt ? <ChatReadReceipt readAt={patientReadAt} /> : null}
    </Box>
  )
}

export default ChatMessage
